import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import toast from "react-hot-toast";
import { X, AlertTriangle, Loader2, ArrowRight, Info } from "lucide-react";

/* ------------------------------------------------------------------ */
/*  STATUS CONFIG  (matches the Order model enum)                      */
/* ------------------------------------------------------------------ */
const STATUS_INFO = {
  New: { desc: "Order received and waiting to be reviewed.", dot: "bg-sky-500" },
  Accepted: { desc: "Order confirmed and ready to be processed.", dot: "bg-indigo-500" },
  Processing: { desc: "Stock is reserved and items are being prepared.", dot: "bg-amber-500" },
  Packed: { desc: "Items are packed and ready for the courier.", dot: "bg-orange-500" },
  Shipped: { desc: "Handed to the courier and on the way.", dot: "bg-blue-500" },
  Delivered: { desc: "Delivered to the customer.", dot: "bg-emerald-500" },
  Return: { desc: "Customer is returning the item(s).", dot: "bg-purple-500" },
  Replacement: { desc: "A replacement is being sent to the customer.", dot: "bg-violet-500" },
  Repair: { desc: "Item(s) have gone for repair.", dot: "bg-teal-500" },
  Cancelled: { desc: "Order is cancelled and will not be fulfilled.", dot: "bg-red-500" },
};

const GROUPS = [
  { title: "Fulfilment", items: ["New", "Accepted", "Processing", "Packed", "Shipped", "Delivered"] },
  { title: "After-sales", items: ["Return", "Replacement", "Repair"] },
  { title: "Cancellation", items: ["Cancelled"] },
];

/** Which statuses an order may move to from its current one. */
const NEXT = {
  New: ["Accepted", "Cancelled"],
  Accepted: ["Processing", "Cancelled"],
  Processing: ["Packed", "Cancelled"],
  Packed: ["Shipped", "Cancelled"],
  Shipped: ["Delivered"],
  Delivered: ["Return", "Replacement", "Repair"],
  Return: ["Replacement", "Repair"],
  Replacement: ["Delivered"],
  Repair: ["Delivered"],
  Cancelled: [],
};

const shortId = (id = "") => id.slice(-6).toUpperCase();

/* ------------------------------------------------------------------ */
/*  COMPONENT                                                          */
/* ------------------------------------------------------------------ */
const StatusChangeModal = ({ isOpen, onClose, order, refreshOrders }) => {
  const [selectedStatus, setSelectedStatus] = useState("");
  const [podUrl, setPodUrl] = useState("");
  const [podError, setPodError] = useState("");
  const [showAll, setShowAll] = useState(false);
  const [loading, setLoading] = useState(false);

  const current = order?.status || "New";
  const allowed = useMemo(() => NEXT[current] || [], [current]);
  const nextStep = allowed.find((s) => s !== "Cancelled") || allowed[0];

  const isCancel = selectedStatus === "Cancelled";
  const usesCancelEndpoint = isCancel && current === "New"; // restores stock on the server
  const unchanged = !selectedStatus || selectedStatus === current;

  const customer =
    order?.user && typeof order.user === "object"
      ? `${order.user.firstName || ""} ${order.user.lastName || ""}`.trim()
      : "";

  const close = () => {
    if (!loading) onClose();
  };

  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e) => {
      if (e.key === "Escape" && !loading) onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [isOpen, loading, onClose]);

  if (!isOpen || !order) return null;

  const isEnabled = (s) => s !== current && (showAll || allowed.includes(s));

  const handleUpdateStatus = async () => {
    if (!selectedStatus) {
      toast.error("Please select a status before updating");
      return;
    }

    const pod = podUrl.trim();
    if (selectedStatus === "Delivered" && pod && !/^https?:\/\/\S+$/i.test(pod)) {
      setPodError("Enter a valid link starting with http:// or https://");
      return;
    }

    setLoading(true);
    try {
      const base = process.env.REACT_APP_API_URL;
      const response = usesCancelEndpoint
        ? await axios.put(`${base}/orders/cancel-order/${order._id}`)
        : await axios.put(`${base}/orders/update-order-status/${order._id}`, {
            status: selectedStatus,
            ...(selectedStatus === "Delivered" && pod ? { pod_img_url: pod } : {}),
          });

      toast.success(response.data?.message || "Order status updated successfully");
      await refreshOrders();
      onClose();
    } catch (err) {
      console.error("Error updating order status:", err);
      toast.error(err.response?.data?.message || "Failed to update order status");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
      onMouseDown={(e) => e.target === e.currentTarget && close()}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="status-modal-title"
        className="w-full max-w-lg max-h-[92vh] flex flex-col bg-white rounded-2xl shadow-2xl border border-slate-200 overflow-hidden"
      >
        {/* Header */}
        <header className="flex items-start justify-between gap-4 px-6 py-4 bg-slate-50 border-b border-slate-200">
          <div className="min-w-0">
            <h2 id="status-modal-title" className="text-lg font-bold text-white">
              Update order status
            </h2>
            <p className="text-sm text-slate-500 mt-0.5 truncate">
              Order #{shortId(order._id)}
              {customer && ` · ${customer}`}
            </p>
          </div>
          <button
            onClick={close}
            aria-label="Close"
            className="p-1.5 -mr-1.5 rounded-lg text-slate-500 hover:bg-slate-200/70 hover:text-slate-800 transition-colors"
          >
            <X size={20} />
          </button>
        </header>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">
          {/* Current status */}
          <div className="flex items-center gap-3 text-sm">
            <span className="text-slate-500">Current status</span>
            <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-slate-100 text-slate-800 font-semibold">
              <span className={`w-2 h-2 rounded-full ${STATUS_INFO[current]?.dot || "bg-slate-400"}`} />
              {current}
            </span>
          </div>

          {allowed.length === 0 && !showAll && (
            <div className="flex gap-2.5 rounded-lg bg-slate-50 border border-slate-200 px-4 py-3 text-sm text-slate-600">
              <Info size={16} className="mt-0.5 shrink-0 text-slate-400" />
              <p>This order is {current.toLowerCase()} and has no further steps.</p>
            </div>
          )}

          {/* Options */}
          <fieldset className="space-y-4" disabled={loading}>
            <legend className="sr-only">Choose the new status</legend>
            {GROUPS.map((group) => {
              const items = group.items.filter((s) => showAll || s === current || allowed.includes(s));
              if (!items.length) return null;
              return (
                <div key={group.title}>
                  <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-400 mb-2">
                    {group.title}
                  </p>
                  <div className="space-y-2">
                    {items.map((s) => {
                      const enabled = isEnabled(s);
                      const selected = selectedStatus === s;
                      const isCurrent = s === current;
                      return (
                        <label
                          key={s}
                          title={!enabled && !isCurrent ? `Not available from ${current}` : undefined}
                          className={`flex items-start gap-3 rounded-lg border px-4 py-3 transition-colors ${
                            selected
                              ? "border-[#e47911] bg-amber-50 ring-1 ring-[#e47911]"
                              : enabled
                              ? "border-slate-300 hover:bg-slate-50 cursor-pointer"
                              : "border-slate-200 bg-slate-50/60 opacity-60 cursor-not-allowed"
                          }`}
                        >
                          <input
                            type="radio"
                            name="orderStatus"
                            value={s}
                            checked={selected}
                            disabled={!enabled}
                            onChange={() => {
                              setSelectedStatus(s);
                              setPodError("");
                            }}
                            className="mt-1 accent-[#e47911]"
                          />
                          <span className="flex-1 min-w-0">
                            <span className="flex flex-wrap items-center gap-2">
                              <span className={`w-2 h-2 rounded-full ${STATUS_INFO[s].dot}`} />
                              <span className="font-semibold text-white text-sm">{s}</span>
                              {isCurrent && (
                                <span className="text-[10.5px] font-semibold px-2 py-0.5 rounded-full bg-slate-200 text-slate-600">
                                  Current
                                </span>
                              )}
                              {s === nextStep && enabled && (
                                <span className="inline-flex items-center gap-1 text-[10.5px] font-semibold px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700">
                                  <ArrowRight size={10} /> Next step
                                </span>
                              )}
                            </span>
                            <span className="block text-[13px] text-slate-500 mt-0.5">{STATUS_INFO[s].desc}</span>
                          </span>
                        </label>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </fieldset>

          {/* Show-all toggle */}
          <div>
            <button
              type="button"
              onClick={() => {
                setShowAll((v) => !v);
                setSelectedStatus("");
              }}
              className="text-sm text-blue-600 hover:text-blue-700 hover:underline"
            >
              {showAll ? "Show only valid next steps" : "Show all statuses"}
            </button>
            {showAll && (
              <p className="flex gap-2 mt-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                <AlertTriangle size={14} className="shrink-0 mt-0.5" />
                Skipping steps can leave stock, payments and reports out of sync. Use this only to correct a mistake.
              </p>
            )}
          </div>

          {/* Delivered: POD */}
          {selectedStatus === "Delivered" && (
            <div>
              <label htmlFor="pod-url" className="block text-sm font-semibold text-slate-800">
                Proof of delivery <span className="font-normal text-slate-400">(optional)</span>
              </label>
              <input
                id="pod-url"
                type="url"
                value={podUrl}
                onChange={(e) => {
                  setPodUrl(e.target.value);
                  setPodError("");
                }}
                placeholder="https://… link to the delivery photo"
                className={`mt-1.5 w-full rounded-lg border px-3 py-2 text-sm text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 ${
                  podError
                    ? "border-red-400 focus:ring-red-200"
                    : "border-slate-300 focus:ring-[#d4af37]/50 focus:border-transparent"
                }`}
              />
              {podError && <p className="mt-1 text-xs text-red-600">{podError}</p>}
            </div>
          )}

          {/* Cancelled: warning */}
          {isCancel && (
            <div className="flex gap-2.5 rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-800">
              <AlertTriangle size={16} className="mt-0.5 shrink-0" />
              <div>
                <p className="font-semibold">Cancel this order?</p>
                <p className="mt-0.5 text-red-700">
                  {usesCancelEndpoint
                    ? "The reserved stock will be returned to inventory automatically."
                    : "Stock is not returned automatically for orders that were already accepted. Fix it afterwards in Inventory › Stock Adjustment."}
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <footer className="flex items-center justify-end gap-3 px-6 py-4 bg-slate-50 border-t border-slate-200">
          <button
            onClick={close}
            disabled={loading}
            className="px-4 py-2 rounded-lg text-sm font-medium text-slate-700 bg-white border border-slate-300 hover:bg-slate-50 disabled:opacity-60 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleUpdateStatus}
            disabled={loading || unchanged}
            className={`inline-flex items-center gap-2 px-5 py-2 rounded-lg text-sm font-medium shadow-sm border transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
              isCancel
                ? "text-white bg-red-600 border-red-700 hover:bg-red-700"
                : "text-white border-transparent bg-[#b07d17] hover:bg-[#8c6312]"
            }`}
          >
            {loading && <Loader2 size={15} className="animate-spin" />}
            {loading ? "Updating…" : isCancel ? "Cancel order" : selectedStatus ? `Mark as ${selectedStatus}` : "Update status"}
          </button>
        </footer>
      </div>
    </div>
  );
};

export default StatusChangeModal;